import _ from "lodash";
import { get, isEqual, sortBy, fromPairs, map } from "lodash";
import { toast } from "react-toastify";

/** @function getMinMaxFromObject
 *  @param {Object} targetObject - Object with values of numbers
 * @return {Object} Object with min and max Values
 */
const getMinMaxFromObject = (targetObject) => {
  const arr = Object.values(targetObject);
  try {
    let min = Math.min(...arr);
    let max = Math.max(...arr);
    return { min: min, max: max };
  } catch (error) {
    console.log("error: cannot find min max in object", error.message);
    return { min: 0, max: 0 };
  }
};

const capitalizeFirst = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const removeTimestamps = (obj) => {
  let cloned = { ...obj };
  delete cloned["updated_at"];
  delete cloned["updated_at_readable"];
  delete cloned["timestamp"];
  delete cloned["timestamp_readable"];
  return cloned;
};

/* @function arrayToMap - helper function to generate Map from Array
 * @param {Array} data - data-item MUST HAVE key property
 * @return {Map} parsed Map data hash_key is the map key
 */
const arrayToMap = (data, key = "hash_key") => {
  const map = new Map();
  //console.log("array to map", data, key);
  if (!data) return;
  data.forEach((o) => {
    /*if (o["Exception"]) {
      console.log("object", o)
      let newObject = o['plain_entry'] || {}
      newObject['Exception'] = o['Exception']
      o = newObject
      console.log("object", o)
    }*/
    if (!o[key] && !o["hash_id"]) return;
    let mapKey = o[key] || o.hash_id;
    map.set(mapKey, o);
  });
  //console.log("array to map", map);

  return map;
};
/** objectDifference - simple comparison of 2 objects
 *  both objects should have same signature (keys)
 *   and return keys which values are not equal
 *   @param {object} a -
 *   @param {object} b -
 *   @return {array} array list of keys which values differ
 */
const objectDifference = (a, b) => {
  let diff = _.reduce(
    a,
    (result, value, key) =>
      isEqual(value, b[key]) ? result : result.concat(key),
    []
  );
  return diff;
};
/** deepObjectDiffere - in one direction from a -> before(() => {
 *
 */
const deepObjectDifference = (obj1, obj2) => {
  var result = {};
  for (let key in obj1) {
    if (obj2[key] !== obj1[key]) result[key] = obj1[key];

    if (typeof obj2[key] == "array" && typeof obj1[key] == "array")
      result[key] = deepObjectDifference(obj1[key], obj2[key]);

    if (typeof obj2[key] == "object" && typeof obj1[key] == "object")
      result[key] = deepObjectDifference(obj1[key], obj2[key]);
  }
  return result;
};

/** generateOptions - helper fo select options
 * uses lowdash get to access nested object by string
 * @param {array} data - array of objects to iterate
 * @param {string} dataKey - string of key used to get value in data obj
 * @return {array} - array of objects for Select.Option generation
 *
 */
const generateOptions = (data, dataKey) => {
  let ar = [];
  data.forEach((value, key) => {
    // if(!value || !value.hasOwnProperty(dataKey)) return
    ar.push({
      key: key,
      value: key,
      text: get(value, dataKey),
    });
  });
  return ar;
};
/**
 * similar as above
 * @param {Array} dataObjects   array of objects
 * @param {String} keyAccessor   name for react dom-key
 * @param {String} textAccessor  display name for option
 * @param {String} valueAccessor
 */
const generateDropdownOptions = ({
  dataObjects,
  keyAccessor = "hash_key",
  textAccessor = "name",
  valueAccessor = "hash_key",
}) => {
  if (!dataObjects) return;
  let objArray = [];
  dataObjects.forEach((data) => {
    objArray.push({
      key: _.get(data, keyAccessor),
      text: _.get(data, textAccessor),
      value: _.get(data, valueAccessor),
    });
  });
  return objArray;
};

const sortByKeys = (object) => {
  const keys = Object.keys(object);
  const sortedKeys = sortBy(keys);

  return fromPairs(map(sortedKeys, (key) => [key, object[key]]));
};
const alphabetString = "abcdefghijklmnopqrstuvwxyz";

/** returns BoundingClientRect of el */
function getElementDimensions(element) {
  const rect = element.getBoundingClientRect();
  const style = window.getComputedStyle(element);
  const margins = {
    left: parseInt(style.marginLeft),
    right: parseInt(style.marginRight),
  };
  const padding = {
    left: parseInt(style.paddingLeft),
    right: parseInt(style.paddingRight),
  };
  return {
    left: Math.ceil(rect.left),
    width: Math.ceil(rect.width),
    outerWidth: Math.ceil(
      rect.width + margins.left + margins.right + padding.left + padding.right
    ),
    marginLeft: margins.left,
    marginRight: margins.right,
    paddingLeft: padding.left,
    paddingRight: padding.right,
    scrollWidth: element.scrollWidth,
  };
}

/**
 * Normalize axios errors for Redux/UI. Handles JSON API bodies, plain text,
 * and HTML error pages from API Gateway (e.g. 404 with <!DOCTYPE ...>).
 * @param {*} error - axios error or similar
 * @returns {{ message: string, status_code: number }}
 */
const getRestApiErrorResponse = (error) => {
  if (error?.code === "ECONNABORTED") {
    return { message: "Request timed out. Please try again.", status_code: 408 };
  }

  const status = error?.response?.status;
  const data = error?.response?.data;

  if (error?.response) {
    if (data && typeof data === "object" && !Array.isArray(data)) {
      const message =
        (typeof data.message === "string" && data.message) ||
        (typeof data.error === "string" && data.error) ||
        "Request failed";
      const status_code = Number(data.status_code) || status || 0;
      return { message, status_code };
    }

    if (typeof data === "string") {
      const trimmed = data.trim();
      const isHtml =
        trimmed.startsWith("<!DOCTYPE") ||
        trimmed.startsWith("<!doctype") ||
        trimmed.startsWith("<html");
      if (isHtml) {
        if (status === 404) {
          return {
            message:
              "This API endpoint was not found (404). It may be missing from the server or the URL path may be wrong.",
            status_code: 404,
          };
        }
        return {
          message: `The server returned an error page (${status ?? "unknown"}).`,
          status_code: status || 0,
        };
      }
      return {
        message:
          trimmed.length > 200 ? `${trimmed.slice(0, 200)}…` : trimmed || "Request failed",
        status_code: status || 0,
      };
    }

    return {
      message: status ? `Request failed (${status})` : "Request failed.",
      status_code: status || 0,
    };
  }

  if (error?.request && !error?.response) {
    return {
      message: "No response from server. Please check your connection.",
      status_code: 0,
    };
  }

  return {
    message: error?.message || "An unexpected error occurred.",
    status_code: 0,
  };
};

const handelError = (error) => {
  const { message } = getRestApiErrorResponse(error);
  toast.error(message);
};

export {
  alphabetString,
  arrayToMap,
  capitalizeFirst,
  getElementDimensions,
  getRestApiErrorResponse,
  objectDifference,
  deepObjectDifference,
  generateOptions,
  generateDropdownOptions,
  getMinMaxFromObject,
  removeTimestamps,
  sortByKeys,
  handelError
};
